import { useEffect, useRef, useState } from 'react'

export const useVerificationCode = (
  value: string,
  onChange: (newValue: string) => void
) => {
  const length = 4
  const [inputValues, setInputValues] = useState<string[]>(
    new Array(length).fill('')
  )
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0]?.focus()
    }
  }, [])

  useEffect(() => {
    setInputValues(
      value
        .split('')
        .slice(0, length)
        .concat(new Array(length - value.length).fill(''))
    )
  }, [value])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    let newValue = e.target.value

    // Allow only a single character and ensure it's a numeric value
    if (newValue.length > 1) {
      newValue = newValue.charAt(0) // Keep only the first character if multiple characters are entered
    }

    if (!isNaN(Number(newValue)) && newValue !== '') {
      const newInputValues = [...inputValues]
      newInputValues[index] = newValue
      setInputValues(newInputValues)

      // Pass the updated values to the parent component by joining them
      onChange(newInputValues.join(''))

      // Automatically focus on the next input if the current value is valid
      if (index < length - 1 && newValue !== '') {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      handleClear(index)
    }
  }

  const handleClear = (index: number) => {
    if (inputValues[index] === '') {
      focusLastNonEmptyField(index)
    } else {
      clearCurrentField(index)
    }
  }

  const focusLastNonEmptyField = (index: number) => {
    for (let i = index - 1; i >= 0; i--) {
      if (inputValues[i] !== '') {
        inputRefs.current[i]?.focus()
        break
      }
    }
  }

  const clearCurrentField = (index: number) => {
    const newInputValues = [...inputValues]
    newInputValues[index] = ''
    setInputValues(newInputValues)
    onChange(newInputValues.join(''))
    focusLastNonEmptyField(index)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Ensures the cursor is placed at the beginning
    e.target.setSelectionRange(0, 1)
  }

  return {
    inputValues,
    handleChange,
    handleKeyDown,
    inputRefs,
    handleFocus,
  }
}
